import React from 'react';
import { useState } from 'react';
import { useEffect } from 'react';
import { useNetwork } from 'services/network';
import { getBrand } from 'entities/agenda/agendaHelper';
import {
  Button,
  CircularProgress,
  Grid,
  Stack,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { Controller, useForm } from 'react-hook-form';
import { useIntl } from 'react-intl';
import translations from 'utils/translations';
import { format } from 'date-fns';
import { FilterSelect } from 'pages/exhibitorsV2/Homepage/components';
import { SdmTextfieldV5 } from '../../styles';
import { SdmCheckboxV5 } from 'components/SdmCheckboxV5';
import { EmptyVisitDatesOptions } from './emptyVisitsData';
import TimeInput from './TimeInput/TimeInput';

const defaultValue = {
  id: undefined,
  brandName: undefined,
  date: undefined,
  time: undefined,
  language: undefined,
  givenName: undefined,
  familyName: undefined,
  email: undefined,
  phone: undefined,
  company: undefined,
  reason: undefined,
  slotTime: undefined,
  morningStart: undefined,
};

const AppointmentContent = ({ user, brandId, onSubmit, hasVisits }) => {
  const intl = useIntl();
  const i18n = translations.Agenda(intl).forms;
  const i18nPrivacy = translations.Tickets(intl).edit.ticketEditCard.fields;
  const i18nAgenda = translations.Agenda(intl).agenda.create;
  const theme = useTheme();
  const isSmDown = useMediaQuery(theme.breakpoints.down('sm'));
  const network = useNetwork();
  // Hooks
  const { handleSubmit, errors, control, watch, setValue, getValues } = useForm(
    {
      defaultValues: defaultValue,
    }
  );

  const selectedDate = watch('date');

  const [fetchedBrand, setFetchedBrand] = useState();
  const [fetchedUser, setFetchedUser] = useState();

  const [isLoading, setIsLoading] = useState(true);
  const [dates, setDates] = useState([]);
  const [visits, setVisits] = useState([]);
  const [times, setTimes] = useState([]);
  const [checkValue, setCheckValue] = useState(false);
  const [customCheckError, setCustomCheckError] = useState(false);

  useEffect(() => {
    const fetchUser = async (id) => {
      try {
        const promises = [];
        const input = {
          userSalesforceId: user.salId,
        };
        promises.push(network.agendaLambda(input, '/user'));
        if (!fetchedBrand && id) {
          promises.push(network.exhibitorGet(id));
          promises.push(
            getBrand({
              id: id,
            })
          );
          const res = await Promise.all(promises);
          const _user = res[0];
          setFetchedUser(_user);
          const brand = res[1];
          const exhibitorData = res[2];

          if (brand) {
            setFetchedBrand({ ...brand, data: exhibitorData });
            saveTheDate({ ...brand, data: exhibitorData });
          }
        } else {
          fetchedBrand({});
        }
      } catch (error) {
        console.log('LOG::  ~ error', error);
      } finally {
        setIsLoading(false);
      }
    };

    const fetchLoggedUserOnly = async () => {
      try {
        const promises = [];
        const input = {
          userSalesforceId: user.salId,
        };
        promises.push(network.agendaLambda(input, '/user'));
        const res = await Promise.all(promises);
        const _user = res[0];
        setFetchedUser(_user);
      } catch (error) {
        console.log('LOG::  ~ error', error);
      } finally {
        setIsLoading(false);
      }
    };

    if (user && user.salId && brandId) {
      if (!hasVisits) {
        fetchLoggedUserOnly();
      } else {
        fetchUser(brandId);
      }
    }
  }, [user]);

  const saveTheDate = (exhibitor) => {
    const _v = exhibitor.data.visits.items.filter(
      (v) => !!v?.slots?.items?.length
    );
    setVisits(_v);
    const _dates = exhibitor.data.visits.items
      .filter((v) => !!v?.slots?.items?.length)
      .filter((visit) => {
        const slots = visit.slots.items.filter(
          (s) => s.occupants?.items?.length < parseInt(s.slotCapacity)
        );
        return !!slots?.length;
      })
      .sort((a, b) => new Date(a.date) - new Date(b.date))
      .map((d) => ({
        value: d.date,
        label: format(new Date(d.date), 'dd-MM-yyyy'),
        id: d.id,
      }));
    setDates(_dates);
  };

  const languages = [
    { value: 'it', label: i18n.fields.languages.it },
    { value: 'en', label: i18n.fields.languages.en },
    { value: 'fr', label: i18n.fields.languages.fr },
    { value: 'ru', label: i18n.fields.languages.ru },
    { value: 'ch', label: i18n.fields.languages.ch },
    { value: 'de', label: i18n.fields.languages.de },
  ];

  const singleSlotOptions = [
    { value: 30, label: `30 ${i18nAgenda.fields.minutes}` },
    { value: 60, label: `60 ${i18nAgenda.fields.minutes}` },
  ];

  const renderError = (key) => {
    if (errors[key] && errors[key]?.type === 'required') {
      return (
        <Typography
          style={{ color: `${theme.palette.primary.main}`, marginTop: -0 }}
          variant={'body2'}
        >
          {i18n.fields.error}
        </Typography>
      );
    }
    return null;
  };

  useEffect(() => {
    if (checkValue) setCustomCheckError(false);
  }, [checkValue]);

  const handleCheckRequired = (data) => {
    if (!checkValue) {
      return setCustomCheckError(true);
    }
    onSubmit?.(data);
  };

  return (
    <div>
      {isLoading ? (
        <Stack
          marginY={'50px'}
          alignItems={'center'}
          color={theme.palette.primary.main}
          width={'100%'}
        >
          <CircularProgress color="inherit" />
        </Stack>
      ) : (
        <form
          onSubmit={handleSubmit(handleCheckRequired)}
          style={{ marginTop: '25px' }}
        >
          <Grid container columnSpacing={'25px'} rowSpacing={'25px'}>
            {hasVisits ? (
              <>
                <Grid item md={6} sm={6} xs={12}>
                  <Typography
                    variant={'xs'}
                    sx={{ fontFamily: 'Suisse-Intl !important' }}
                  >
                    {i18n.fields.date} *
                  </Typography>
                  <Controller
                    name="date"
                    control={control}
                    rules={{ required: true }}
                    render={(props) => {
                      return (
                        <FilterSelect
                          isRelative
                          options={dates}
                          value={props.value}
                          labelKey={'label'}
                          label={i18n.fields.date}
                          onSelect={(e) => {
                            props.onChange(e);
                            const visitSelected = visits.find(
                              (v) => v.id === e.id
                            );
                            const slots = visitSelected.slots.items
                              .filter(
                                (s) =>
                                  s.occupants?.items?.length <
                                  parseInt(s.slotCapacity)
                              )
                              .sort(
                                (a, b) => new Date(a.start) - new Date(b.start)
                              )
                              .map((s) => ({
                                label: `${format(
                                  new Date(s.start),
                                  'HH:mm'
                                )}-${format(new Date(s.end), 'HH:mm')}`,
                                value: s.id,
                              }));
                            setTimes(slots);
                            setValue('time', {
                              label: undefined,
                              value: undefined,
                            });
                          }}
                          hideResetLabel
                        />
                      );
                    }}
                  />
                  {renderError('date')}
                </Grid>
                <Grid item md={6} sm={6} xs={12}>
                  <Typography
                    variant={'xs'}
                    sx={{ fontFamily: 'Suisse-Intl !important' }}
                  >
                    {i18n.fields.time} *
                  </Typography>
                  <Controller
                    name="time"
                    control={control}
                    rules={{ required: true }}
                    defaultValue={''}
                    render={(props) => {
                      return (
                        <FilterSelect
                          isRelative
                          options={times}
                          value={props.value}
                          labelKey={'label'}
                          label={i18n.fields.time}
                          onSelect={(e) => {
                            props.onChange(e);
                          }}
                          disabled={!selectedDate}
                          hideResetLabel
                        />
                      );
                    }}
                  />
                  {renderError('time')}
                </Grid>
              </>
            ) : (
              <>
                <Grid item md={3} sm={6} xs={12}>
                  <Typography
                    variant={'xs'}
                    sx={{ fontFamily: 'Suisse-Intl !important' }}
                  >
                    {i18nAgenda.fields.date.label} *
                  </Typography>
                  <Controller
                    name="date"
                    control={control}
                    rules={{ required: true }}
                    render={(props) => {
                      return (
                        <FilterSelect
                          isRelative
                          options={EmptyVisitDatesOptions}
                          value={props.value}
                          labelKey={'label'}
                          label={i18nAgenda.fields.date.label}
                          onSelect={(e) => {
                            props.onChange(e);
                          }}
                          hideResetLabel
                        />
                      );
                    }}
                  />
                  {renderError('date')}
                </Grid>
                <Grid item md={3} sm={6} xs={12}>
                  <Typography
                    variant={'xs'}
                    sx={{ fontFamily: 'Suisse-Intl !important' }}
                  >
                    {i18nAgenda.fields.morningStart.label} *
                  </Typography>
                  <TimeInput
                    control={control}
                    label={i18nAgenda.fields.morningStart.label}
                    theme={theme}
                    getValues={getValues}
                    setValue={setValue}
                    keyName={'morningStart'}
                    customStyle={{ marginLeft: 0 }}
                    isEnd={false}
                    defaultValue={'09:30'}
                  />
                  {renderError('morningStart')}
                </Grid>
                <Grid item md={3} sm={6} xs={12}>
                  <Typography
                    variant={'xs'}
                    sx={{ fontFamily: 'Suisse-Intl !important' }}
                  >
                    {i18nAgenda.fields.slotTime.label} *
                  </Typography>
                  <Controller
                    name="slotTime"
                    control={control}
                    rules={{ required: true }}
                    render={(props) => {
                      return (
                        <FilterSelect
                          isRelative
                          options={singleSlotOptions}
                          value={props.value || singleSlotOptions[0]}
                          labelKey={'label'}
                          label={i18nAgenda.fields.slotTime.label}
                          hideResetLabel
                          onSelect={(e) => {
                            props.onChange(e);
                          }}
                        />
                      );
                    }}
                  />
                  {renderError('slotTime')}
                </Grid>
              </>
            )}
            <Grid item md={hasVisits ? 6 : 3} sm={6} xs={12}>
              <Typography
                variant={'xs'}
                sx={{ fontFamily: 'Suisse-Intl !important' }}
              >
                {i18n.fields.language} *
              </Typography>
              <Controller
                name="language"
                control={control}
                rules={{ required: true }}
                render={(props) => {
                  return (
                    <FilterSelect
                      isRelative
                      options={languages}
                      value={props.value || languages[0]}
                      labelKey={'label'}
                      label={i18n.fields.language}
                      onSelect={(e) => {
                        props.onChange(e);
                      }}
                      hideResetLabel
                    />
                  );
                }}
              />
              {renderError('language')}
            </Grid>
            {hasVisits ? <Grid item md={6} sm={6} xs={false} /> : null}
            <Grid item md={6} sm={6} xs={12}>
              <Controller
                name={'givenName'}
                control={control}
                defaultValue={user.givenName}
                rules={{ required: true }}
                render={(props) => (
                  <SdmTextfieldV5
                    sx={{
                      width: '100%',
                      '& .MuiInputBase-input': {
                        fontFamily: 'Suisse-Intl',
                        fontSize: 16,
                      },
                      '& .MuiFormLabel-root': {
                        fontFamily: 'Suisse-Intl',
                        color: `black`,
                        fontSize: 12,
                      },
                    }}
                    variant="standard"
                    onChange={(e) => {
                      props.onChange(e.target.value);
                    }}
                    value={user.givenName}
                    label={`${i18n.fields.givenName}`}
                    disabled={true}
                  />
                )}
              />
            </Grid>
            <Grid item md={6} sm={6} xs={12}>
              <Controller
                name={'familyName'}
                control={control}
                defaultValue={user.familyName}
                rules={{ required: true }}
                render={(props) => (
                  <SdmTextfieldV5
                    sx={{
                      width: '100%',
                      '& .MuiInputBase-input': {
                        fontFamily: 'Suisse-Intl',
                        fontSize: 16,
                      },
                      '& .MuiFormLabel-root': {
                        fontFamily: 'Suisse-Intl',
                        color: `black`,
                        fontSize: 12,
                      },
                    }}
                    variant="standard"
                    onChange={(e) => {
                      props.onChange(e.target.value);
                    }}
                    value={user.familyName}
                    label={`${i18n.fields.familyName}`}
                    disabled={true}
                  />
                )}
              />
            </Grid>
            <Grid item md={6} sm={6} xs={12}>
              <Controller
                name={'email'}
                control={control}
                defaultValue={user.email}
                rules={{ required: true }}
                render={(props) => (
                  <SdmTextfieldV5
                    sx={{
                      width: '100%',
                      '& .MuiInputBase-input': {
                        fontFamily: 'Suisse-Intl',
                        fontSize: 16,
                      },
                      '& .MuiFormLabel-root': {
                        fontFamily: 'Suisse-Intl',
                        color: `black`,
                        fontSize: 12,
                      },
                    }}
                    variant="standard"
                    onChange={(e) => {
                      props.onChange(e.target.value);
                    }}
                    value={user.email}
                    label={`${i18n.fields.email}`}
                    disabled={true}
                  />
                )}
              />
            </Grid>
            <Grid item md={6} sm={6} xs={12}>
              <Controller
                name={'phone'}
                control={control}
                defaultValue=""
                rules={{ required: true }}
                render={(props) => (
                  <SdmTextfieldV5
                    sx={{
                      width: '100%',
                      '& .MuiInputBase-input': {
                        fontFamily: 'Suisse-Intl',
                        fontSize: 16,
                      },
                      '& .MuiFormLabel-root': {
                        fontFamily: 'Suisse-Intl',
                        color: `black`,
                        fontSize: 12,
                      },
                    }}
                    variant="standard"
                    onChange={(e) => {
                      props.onChange(e.target.value);
                    }}
                    value={props.value}
                    label={`${i18n.fields.phone}*`}
                  />
                )}
              />
              {renderError('phone')}
            </Grid>
            <Grid item xs={12}>
              <Controller
                name={'company'}
                control={control}
                defaultValue={
                  fetchedUser?.ragioneSociale ? fetchedUser?.ragioneSociale : ''
                }
                rules={{ required: true }}
                render={(props) => (
                  <SdmTextfieldV5
                    sx={{
                      width: '100%',
                      '& .MuiInputBase-input': {
                        fontFamily: 'Suisse-Intl',
                        fontSize: 16,
                      },
                      '& .MuiFormLabel-root': {
                        fontFamily: 'Suisse-Intl',
                        color: `black`,
                        fontSize: 12,
                      },
                    }}
                    variant="standard"
                    onChange={(e) => {
                      props.onChange(e.target.value);
                    }}
                    value={
                      fetchedUser?.ragioneSociale
                        ? fetchedUser?.ragioneSociale
                        : props.value
                    }
                    label={`${i18n.fields.company}*`}
                    disabled={fetchedUser?.ragioneSociale}
                  />
                )}
              />
              {renderError('company')}
            </Grid>
            <Grid item xs={12}>
              <Controller
                name="reason"
                control={control}
                rules={{ required: true }}
                defaultValue=""
                render={(props) => (
                  <SdmTextfieldV5
                    sx={{
                      width: '100%',
                      '& .MuiInputBase-input': {
                        fontFamily: 'Suisse-Intl',
                        fontSize: 16,
                      },
                      '& .MuiFormLabel-root': {
                        fontFamily: 'Suisse-Intl',
                        color: `black`,
                        fontSize: 12,
                      },
                    }}
                    variant="standard"
                    onChange={(e) => {
                      props.onChange(e.target.value);
                    }}
                    value={props.value}
                    label={`${i18n.fields.reasonUser}*`}
                    multiline
                    rows={4}
                    maxRows={Infinity}
                  />
                )}
              />
              {renderError('reason')}
            </Grid>
            <Grid item xs={12}>
              <SdmCheckboxV5
                onChange={() => {
                  setCheckValue((prev) => !prev);
                }}
                variant="black"
                value={checkValue}
                isSelected={checkValue}
                label={
                  isSmDown
                    ? i18nPrivacy.owner_flag_privacy_1_mobile.label
                    : i18nPrivacy.owner_flag_privacy_1.label
                }
                labelPlacement={'end'}
                customFont={'text'}
                customStyle={{ alignItems: 'flex-start' }}
                checkboxCustomStyle={{ marginTop: '3px', marginLeft: '4px' }}
              />
              {customCheckError ? (
                <Typography
                  style={{
                    color: `${theme.palette.primary.main}`,
                    marginTop: -0,
                  }}
                  variant={'body2'}
                >
                  {i18n.fields.error}
                </Typography>
              ) : null}
            </Grid>
            <Grid item xs={12}>
              <Button
                style={{
                  height: 44,
                  ...(isSmDown && {
                    width: '100%',
                  }),
                }}
                variant={'contained'}
                color={'primary'}
                size="s"
                type={'submit'}
              >
                {i18n.fields.buttons.sendCustomRequest}
              </Button>
            </Grid>
          </Grid>
        </form>
      )}
    </div>
  );
};
export default AppointmentContent;
